import React, { useRef, useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import {
  X,
  ShoppingCart,
  Briefcase,
  LogOut,
  LogIn,
  UserPlus,
  HelpCircle,
  User,
  ListIcon,
  Heart,
  Plus,
  ChevronRight,
  ChevronLeft,
  AlertTriangle,
  Settings,
  Bell,
  MessageCircle,
  Calendar,
  Plane,
} from 'lucide-react';
import { useAuth } from './AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme, ThemeName } from '../contexts/ThemeContext';
import { Moon, Sun, Sparkles } from 'lucide-react';
import { supabase } from '../lib/db';
import toast from 'react-hot-toast';
import { useNotifications } from '../contexts/NotificationContext';
import { useUnreadCount } from '../lib/chat/hooks/useUnreadCount';
import { ProviderProfileForm } from './ProviderProfileForm';
import { SellerProfileForm } from './SellerProfileForm';
import { GoogleMapsProvider } from './GoogleMapsProvider';

interface SideMenuProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

type UserRole = 'provider' | 'seller' | 'user';

interface User {
  id: string;
  role: UserRole;
  // Add other user properties as needed
}

interface AuthContextType {
  user: User | null;
  logout: () => Promise<void>;
  updateUserData: () => Promise<void>;
}

export const SideMenu: React.FC<SideMenuProps> = ({ isOpen, setIsOpen }) => {
  const { user, logout, updateUserData } = useAuth() as AuthContextType;
  const { t, language, setLanguage } = useLanguage();
  const { theme, setTheme } = useTheme();

  const themeOptions: { value: ThemeName; label: string; icon: React.ReactNode; preview: string }[] = [
    { value: 'midnight', label: t('themeMidnight'), icon: <Sparkles className="w-4 h-4" />, preview: '#030712' },
    { value: 'dark', label: t('themeDark'), icon: <Moon className="w-4 h-4" />, preview: '#18181b' },
    { value: 'light', label: t('themeLight'), icon: <Sun className="w-4 h-4" />, preview: '#f8fafc' },
  ];
  const history = useHistory();
  const nodeRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [editingForm, setEditingForm] = useState<'' | 'provider' | 'seller'>('');
  const [hasProviderProfile, setHasProviderProfile] = useState(false);
  const [hasSellerProfile, setHasSellerProfile] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [pickupLocations, setPickupLocations] = useState<string[]>([]);
  const [showAccountCenter, setShowAccountCenter] = useState(false);
  const [providerDeactivated, setProviderDeactivated] = useState(false);
  const [sellerDeactivated, setSellerDeactivated] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      fetchUserProfiles();
    }
  }, [isOpen, user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  const fetchUserProfiles = async () => {
    if (!user) return;
    
    try {
      const { data: providerProfile, error: providerError } = await supabase
        .from('provider_profiles')
        .select('id, is_deactivated')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();
      
      if (providerError) throw providerError;
      setHasProviderProfile(!!providerProfile);
      setProviderDeactivated(providerProfile?.is_deactivated || false);
      
      const { data: sellerProfile, error: sellerError } = await supabase
        .from('seller_profiles')
        .select('id, location, pickup_locations, is_deactivated')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();
      
      if (sellerError) throw sellerError;
      setHasSellerProfile(!!sellerProfile);
      setSellerDeactivated(sellerProfile?.is_deactivated || false);

      if (sellerProfile) {
        setSelectedLocation(sellerProfile.location || '');
        setPickupLocations(sellerProfile.pickup_locations || []);
      }
    } catch (error) {
      console.error('Error fetching user profiles:', error);
    }
  };

  const handleSwitchToUserProfile = async () => {
    if (!user) return;
    setLoading(true);
    setIsOpen(false);
    try {
      await updateRoleInProfiles('user');
      refreshProfilePage();
    } catch (error) {
      console.error('Error switching to user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchToSellerProfile = async () => {
    if (!user) return;
    setLoading(true);
    setIsOpen(false);
    
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
        
      if (profileError) {
        console.error('Error checking profile:', profileError);
        toast.error('Error checking profile');
        return;
      }
      
      if (!profile) {
        toast.error('No profile found. Please create a user profile first.');
        return;
      }

      const { data: sellerProfile, error: sellerError } = await supabase
        .from('seller_profiles')
        .select('id, location, pickup_locations')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();
      
      if (sellerError) {
        console.error('Error checking seller profile:', sellerError);
        toast.error('Error checking seller profile');
        return;
      }
      
      if (!sellerProfile) {
        setEditingForm('seller');
        return;
      }
      
      setSelectedLocation(sellerProfile.location || '');
      setPickupLocations(sellerProfile.pickup_locations || []);
      await updateRoleInProfiles('seller');
      refreshProfilePage();
    } catch (error) {
      console.error('Error switching to seller profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchToProviderProfile = async () => {
    if (!user) return;
    setLoading(true);
    setIsOpen(false);
    
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
        
      if (profileError) {
        console.error('Error checking profile:', profileError);
        toast.error('Error checking profile');
        return;
      }
      
      if (!profile) {
        toast.error('No profile found. Please create a user profile first.');
        return;
      }

      const { data: providerProfile, error: providerError } = await supabase
        .from('provider_profiles')
        .select('id')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();
      
      if (providerError) {
        console.error('Error checking provider profile:', providerError);
        toast.error('Error checking provider profile');
        return;
      }
      
      if (!providerProfile) {
        setEditingForm('provider');
        return;
      }
      
      await updateRoleInProfiles('provider');
      refreshProfilePage();
    } catch (error) {
      console.error('Error switching to provider profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateRoleInProfiles = async (newRole: UserRole) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role: newRole, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      user.role = newRole;
      await updateUserData();
    } catch (error) {
      console.error('Error switching role:', error);
      toast.error('Failed to switch roles');
      throw error;
    }
  };

  const refreshProfilePage = () => {
    if (window.location.pathname === '/profile') {
    } else {
      history.push('/profile');
    }
  };

  const handleProfileUpdate = async (data: any) => {
    try {
      setLoading(true);
  
      const { data: existingProfile, error: existingProfileError } = await supabase
        .from('provider_profiles')
        .select('id')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();
  
      if (existingProfileError) {
        throw existingProfileError;
      }
  
      let query;
      if (existingProfile) {
        query = supabase
          .from('provider_profiles')
          .update({
            ...data,
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', user.id);
      } else {
        query = supabase.from('provider_profiles').insert([
          {
            ...data,
            user_id: user.id,
            created_at: new Date().toISOString(),
          },
        ]);
      }
  
      const { error } = await query;
  
      if (error) throw error;
  
      setEditingForm('');
      await updateRoleInProfiles('provider');
      refreshProfilePage();
      toast.success(t('profileUpdateSuccess'));
    } catch (error) {
      console.error('Update profile error:', error);
      toast.error(t('profileUpdateFailure'));
    } finally {
      setLoading(false);
    }
  };

  const handleSellerProfileUpdate = async (data: any) => {
    try {
      setLoading(true);
  
      const { data: existingProfile, error: existingProfileError } = await supabase
        .from('seller_profiles')
        .select('id')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();
  
      if (existingProfileError) {
        throw existingProfileError;
      }
  
      let query;
      if (existingProfile) {
        query = supabase
          .from('seller_profiles')
          .update({
            ...data,
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', user.id);
      } else {
        query = supabase.from('seller_profiles').insert([
          {
            ...data,
            user_id: user.id,
            created_at: new Date().toISOString(),
          },
        ]);
      }
  
      const { error } = await query;
  
      if (error) throw error;
  
      setEditingForm('');
      await updateRoleInProfiles('seller');
      refreshProfilePage();
      toast.success(t('profileUpdateSuccess'));
    } catch (error) {
      console.error('Update profile error:', error);
      toast.error(t('profileUpdateFailure'));
    } finally {
      setLoading(false);
    }
  };

  const handleShowAccountCenter = () => {
    setIsOpen(false);
    setShowAccountCenter(true);
  };

  const AccountCenterModal = () => {
    if (!user) return null;

    const [confirmDeleteAccount, setConfirmDeleteAccount] = useState(false);
    const [deactivatingProvider, setDeactivatingProvider] = useState(false);
    const [deactivatingSeller, setDeactivatingSeller] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const modalRef = useRef<HTMLDivElement>(null);
    
    const handleCloseModal = (e: React.MouseEvent<HTMLElement>) => {
      if (e) {
        e.stopPropagation();
        e.nativeEvent?.stopImmediatePropagation?.();
      }
      setShowAccountCenter(false);
    };
    
    useEffect(() => {
      const handleEscKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setShowAccountCenter(false);
        }
      };
      
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }, []);
    
    const handleDeactivateProvider = async () => {
      try {
        setModalLoading(true);
        
        const { error } = await supabase
          .from('provider_profiles')
          .update({ is_deactivated: true })
          .eq('user_id', user.id);
          
        if (error) throw error;
        
        toast.success(t('providerProfileDeactivated', 'Provider profile deactivated'));
        setDeactivatingProvider(false);
        
        try {
          await fetchUserProfiles();
        } catch (error) {
          console.error('Error refreshing profiles:', error);
        }
      } catch (error) {
        console.error('Error deactivating provider profile:', error);
        toast.error(t('errorDeactivatingProfile', 'Error deactivating profile'));
      } finally {
        setModalLoading(false);
      }
    };
    
    const handleDeactivateSeller = async () => {
      try {
        setModalLoading(true);
        
        const { error } = await supabase
          .from('seller_profiles')
          .update({ is_deactivated: true, updated_at: new Date().toISOString() })
          .eq('user_id', user.id);
          
        if (error) throw error;
        
        toast.success(t('sellerProfileDeactivated', 'Seller profile deactivated'));
        setDeactivatingSeller(false);
        
        if (user.role === 'seller') {
          await updateRoleInProfiles('seller' as UserRole);
        }
        
        try {
          await fetchUserProfiles();
          await updateUserData();
        } catch (error) {
          console.error('Error refreshing profiles:', error);
        }
      } catch (error) {
        console.error('Error deactivating seller profile:', error);
        toast.error(t('errorDeactivatingProfile', 'Error deactivating profile'));
      } finally {
        setModalLoading(false);
      }
    };
    
    const handleDeleteAccount = async () => {
      try {
        setModalLoading(true);
        
        const { error } = await supabase.functions.invoke('delete-account', {
          body: { user_id: user.id } as { user_id: string }
        });
        
        if (error) throw error;
        
        setShowAccountCenter(false);
        toast.success(t('accountDeleted', 'Your account has been successfully deleted.'));
        await logout();
      } catch (error) {
        console.error('Error deleting account:', error);
        toast.error(t('errorDeletingAccount', 'Error deleting account. Please try again.'));
      } finally {
        setModalLoading(false);
      }
    };

    const handleToggleProviderActivation = async () => {
      try {
        setModalLoading(true);
        
        const newStatus = !providerDeactivated;
        
        const { error } = await supabase
          .from('provider_profiles')
          .update({ 
            is_deactivated: !providerDeactivated, 
            updated_at: new Date().toISOString() 
          })
          .eq('user_id', user.id);
          
        if (error) throw error;
        
        toast.success(newStatus ? 'Provider profile activated' : 'Provider profile deactivated');
        
        setDeactivatingProvider(false);
        setProviderDeactivated(!providerDeactivated);
        
        if (providerDeactivated && user.role === 'user') {
          await updateRoleInProfiles('provider');
        }
        else if (!providerDeactivated && user.role === 'provider') {
          await updateRoleInProfiles('user');
        }
        
        try {
          await fetchUserProfiles();
          await updateUserData();
        } catch (error) {
          console.error('Error refreshing profiles:', error);
        }
      } catch (error) {
        console.error('Error toggling provider profile activation:', error);
        toast.error('Error updating profile status');
      } finally {
        setModalLoading(false);
      }
    };
    
    const handleToggleSellerActivation = async () => {
      try {
        setModalLoading(true);
        
        const newStatus = !sellerDeactivated;
        
        const { error } = await supabase
          .from('seller_profiles')
          .update({ 
            is_deactivated: !sellerDeactivated, 
            updated_at: new Date().toISOString() 
          })
          .eq('user_id', user.id);
          
        if (error) throw error;
        
        toast.success(newStatus ? 'Seller profile activated' : 'Seller profile deactivated');
        
        setDeactivatingSeller(false);
        setSellerDeactivated(!sellerDeactivated);
        
        if (sellerDeactivated && user.role === 'user') {
          await updateRoleInProfiles('seller' as UserRole);
        }
        else if (!sellerDeactivated && user.role === 'seller') {
          await updateRoleInProfiles('user' as UserRole);
        }
        
        try {
          await fetchUserProfiles();
          await updateUserData();
        } catch (error) {
          console.error('Error refreshing profiles:', error);
        }
      } catch (error) {
        console.error('Error toggling seller profile activation:', error);
        toast.error('Error updating profile status');
      } finally {
        setModalLoading(false);
      }
    };
    
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 overflow-y-auto"
        onClick={handleCloseModal}
      >
        <div 
          className="bg-white rounded-lg shadow-lg w-full max-w-md overflow-auto max-h-[90vh]" 
          onClick={(e) => e.stopPropagation()}
          ref={modalRef}
        >
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="text-xl font-bold">{t('accountCenter', 'Account Center')}</h2>
            <button 
              onClick={handleCloseModal}
              className="p-1 rounded-full hover:bg-gray-100"
              type="button"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-6 relative">
            {modalLoading && (
              <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary-600 border-t-transparent"></div>
              </div>
            )}
            
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">{t('manageProfiles', 'Manage Profiles')}</h3>
              
              <div className="mb-4 p-3 bg-blue-50 rounded-md text-sm text-blue-800">
                <p>{t('profileManagementInfo', 'Profile management lets you control which aspects of your account are visible. Deactivating a profile hides it but keeps your data intact.')}</p>
              </div>
              
              {hasProviderProfile && (
                <div className="bg-gray-50 p-4 rounded-lg mb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Briefcase className="w-5 h-5 text-gray-600 mr-2" />
                      <span>{t('providerProfile', 'Provider Profile')}</span>
                    </div>
                    <button
                      onClick={() => setDeactivatingProvider(true)}
                      className={`px-3 py-1 ${providerDeactivated ? 
                        'bg-green-100 text-green-800 hover:bg-green-200' : 
                        'bg-amber-100 text-amber-800 hover:bg-amber-200'} rounded-md text-sm`}
                      type="button"
                      disabled={modalLoading || deactivatingProvider}
                    >
                      {providerDeactivated ? t('activate', 'Activate') : t('deactivate', 'Deactivate')}
                    </button>
                  </div>
                  
                  <p className="text-xs text-gray-600 mt-2">
                    {providerDeactivated 
                      ? t('providerProfileDeactivatedInfo', 'Your provider profile is currently deactivated. Activate it to make your services visible.')
                      : t('providerProfileInfo', 'Your provider profile allows you to offer services to customers. Deactivating will hide your services from search results.')}
                  </p>
                  
                  {deactivatingProvider && (
                    <div className="mt-3 bg-white p-3 rounded-md border border-amber-200">
                      <p className="text-sm mb-2">
                        {providerDeactivated 
                          ? t('activateProviderConfirmation', 'Are you sure you want to activate your provider profile? It will become visible in search results.')
                          : t('deactivateProviderConfirmation', 'Are you sure you want to deactivate your provider profile? It will be hidden from search results.')}
                      </p>
                      <div className="flex justify-end space-x-2 mt-2">
                        <button
                          onClick={() => setDeactivatingProvider(false)}
                          className="px-3 py-1 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 text-sm"
                          type="button"
                          disabled={modalLoading}
                        >
                          {t('cancel', 'Cancel')}
                        </button>
                        <button
                          onClick={handleToggleProviderActivation}
                          className={`px-3 py-1 ${providerDeactivated ? 'bg-green-500 hover:bg-green-600' : 'bg-amber-500 hover:bg-amber-600'} text-white rounded-md text-sm`}
                          type="button"
                          disabled={modalLoading}
                        >
                          {modalLoading ? t('processing', 'Processing...') : (providerDeactivated ? t('confirmActivate', 'Confirm Activation') : t('confirmDeactivate', 'Confirm Deactivation'))}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {hasSellerProfile && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <ShoppingCart className="w-5 h-5 text-gray-600 mr-2" />
                      <span>{t('sellerProfile', 'Seller Profile')}</span>
                    </div>
                    <button
                      onClick={() => setDeactivatingSeller(true)}
                      className={`px-3 py-1 ${sellerDeactivated ? 
                        'bg-green-100 text-green-800 hover:bg-green-200' : 
                        'bg-amber-100 text-amber-800 hover:bg-amber-200'} rounded-md text-sm`}
                      type="button"
                      disabled={modalLoading || deactivatingSeller}
                    >
                      {sellerDeactivated ? t('activate', 'Activate') : t('deactivate', 'Deactivate')}
                    </button>
                  </div>
                  
                  <p className="text-xs text-gray-600 mt-2">
                    {sellerDeactivated 
                      ? t('sellerProfileDeactivatedInfo', 'Your seller profile is currently deactivated. Activate it to make your listings visible.')
                      : t('sellerProfileInfo', 'Your seller profile allows you to list items for sale. Deactivating will hide your listings from the marketplace.')}
                  </p>
                  
                  {deactivatingSeller && (
                    <div className="mt-3 bg-white p-3 rounded-md border border-amber-200">
                      <p className="text-sm mb-2">
                        {sellerDeactivated 
                          ? t('activateSellerConfirmation', 'Are you sure you want to activate your seller profile? Your listings will become visible in the marketplace.')
                          : t('deactivateSellerConfirmation', 'Are you sure you want to deactivate your seller profile? Your listings will be hidden from the marketplace.')}
                      </p>
                      <div className="flex justify-end space-x-2 mt-2">
                        <button
                          onClick={() => setDeactivatingSeller(false)}
                          className="px-3 py-1 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 text-sm"
                          type="button"
                          disabled={modalLoading}
                        >
                          {t('cancel', 'Cancel')}
                        </button>
                        <button
                          onClick={handleToggleSellerActivation}
                          className={`px-3 py-1 ${sellerDeactivated ? 'bg-green-500 hover:bg-green-600' : 'bg-amber-500 hover:bg-amber-600'} text-white rounded-md text-sm`}
                          type="button"
                          disabled={modalLoading}
                        >
                          {modalLoading ? t('processing', 'Processing...') : (sellerDeactivated ? t('confirmActivate', 'Confirm Activation') : t('confirmDeactivate', 'Confirm Deactivation'))}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {!hasProviderProfile && !hasSellerProfile && (
                <p className="text-sm text-gray-600 italic">
                  {t('noProfilesToManage', "You don't have any additional profiles to manage.")}
                </p>
              )}
            </div>
            
            <div className="border-t pt-5">
              <h3 className="text-lg font-semibold mb-3 text-red-600 flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2" />
                {t('dangerZone', 'Danger Zone')}
              </h3>
              
              <div className="mb-4 p-3 bg-red-50 rounded-md text-sm text-red-800">
                <p>{t('dangerZoneWarning', 'Actions in this section can permanently affect your account and data. Please proceed with caution and make sure you understand the consequences.')}</p>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                    <span className="text-red-600 font-medium">{t('deleteAccount', 'Delete Account')}</span>
                  </div>
                  <button
                    onClick={() => setConfirmDeleteAccount(true)}
                    className="px-3 py-1 bg-red-100 text-red-800 rounded-md hover:bg-red-200 text-sm"
                    type="button"
                    disabled={modalLoading || confirmDeleteAccount}
                  >
                    {t('delete', 'Delete')}
                  </button>
                </div>
                
                {confirmDeleteAccount && (
                  <div className="mt-3 bg-white p-3 rounded-md border border-red-200">
                    <p className="text-sm mb-2 text-red-600 font-semibold flex items-center">
                      <AlertTriangle className="w-4 h-4 mr-1 flex-shrink-0" />
                      {t('deleteAccountWarning', 'Warning: This action cannot be undone!')}
                    </p>
                    
                    <p className="text-sm mb-3">
                      {t('deleteAccountConfirmation', 'Deleting your account will permanently remove all your data, including:')}
                    </p>
                    
                    <ul className="text-sm list-disc pl-5 mb-3 text-gray-800">
                      <li>{t('deleteAccountPoint1', 'All profile information and settings')}</li>
                      <li>{t('deleteAccountPoint2', 'Listings and services you have created')}</li>
                      <li>{t('deleteAccountPoint3', 'Message history and connections')}</li>
                      <li>{t('deleteAccountPoint4', 'All activity and transaction history')}</li>
                    </ul>
                    
                    <div className="flex justify-end space-x-2 mt-2">
                      <button
                        onClick={() => setConfirmDeleteAccount(false)}
                        className="px-3 py-1 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 text-sm"
                        type="button"
                        disabled={modalLoading}
                      >
                        {t('cancel', 'Cancel')}
                      </button>
                      <button
                        onClick={handleDeleteAccount}
                        className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                        type="button"
                        disabled={modalLoading}
                      >
                        {modalLoading ? t('processing', 'Processing...') : t('confirmDelete', 'Confirm Delete')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const { unreadCount: notifUnread } = useNotifications();
  const chatUnread = useUnreadCount(user?.id || '');

  const navLinks = user ? [
    { to: '/notifications', label: t('notifications', 'Notifications'), icon: <Bell className="w-5 h-5" />, badge: notifUnread },
    { to: '/inbox', label: t('messages', 'Messages'), icon: <MessageCircle className="w-5 h-5" />, badge: chatUnread },
    { to: '/bookings', label: t('appointments', 'Appointments'), icon: <Calendar className="w-5 h-5" />, badge: 0 },
    { to: '/saved', label: t('savedItems', 'Saved Items'), icon: <Heart className="w-5 h-5" />, badge: 0 },
    { to: '/purchases', label: t('myPurchases', 'My Purchases'), icon: <ShoppingCart className="w-5 h-5" />, badge: 0 },
    { to: '/store-orders', label: t('storeOrders', 'Store Orders'), icon: <ListIcon className="w-5 h-5" />, badge: 0 },
    { to: '/senders-travelers', label: t('sendersTravelers', 'Find Travelers & Send Items'), icon: <Plane className="w-5 h-5" />, badge: 0 }
  ] : [
    { to: '/senders-travelers', label: t('sendersTravelers', 'Find Travelers & Send Items'), icon: <Plane className="w-5 h-5" />, badge: 0 }
  ];

  const authLinks = user
    ? [
        { label: t('accountCenter', 'Account Center'), icon: <Settings className="w-5 h-5" />, onClick: handleShowAccountCenter },
        { label: t('logout', 'Logout'), icon: <LogOut className="w-5 h-5" />, onClick: logout }
      ]
    : [
        { to: '/login', label: t('login', 'Login'), icon: <LogIn className="w-5 h-5" /> },
        { to: '/signup', label: t('signup', 'Signup'), icon: <UserPlus className="w-5 h-5" /> },
        { to: '/onboarding?force=true', label: t('backToOnboarding', 'Back to Onboarding'), icon: <ChevronLeft className="w-5 h-5" /> },
      ];

  const footerLinks = [
    { to: '/contact', label: t('contactUs'), icon: <HelpCircle className="w-5 h-5" /> },
    { to: '/help', label: t('helpCenter'), icon: <HelpCircle className="w-5 h-5" /> },
    { to: '/faq', label: t('faq'), icon: <HelpCircle className="w-5 h-5" /> },
    { to: '/privacy', label: t('privacyPolicy'), icon: <HelpCircle className="w-5 h-5" /> },
    { to: '/terms', label: t('termsOfService'), icon: <HelpCircle className="w-5 h-5" /> },
    { to: '/cookies', label: t('cookiePolicy'), icon: <HelpCircle className="w-5 h-5" /> },
    
  ];

  return (
    <>
      <style>
        {`
          .side-menu-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 1050;
            opacity: 0;
            pointer-events: none;
            transition: opacity 300ms ease;
          }
          .side-menu-overlay--visible {
            opacity: 1;
            pointer-events: auto;
          }
          .side-menu {
            position: fixed;
            top: 0;
            right: 0;
            z-index: 1100;
            width: 80%;
            max-width: 360px;
            height: 100%;
            background-color: var(--theme-bg-soft);
            box-shadow: -8px 0 24px rgba(0, 0, 0, 0.45);
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
            overflow-y: auto;
            transform: translateX(100%);
            opacity: 0;
            pointer-events: none;
            transition: transform 300ms ease-in-out, opacity 300ms ease-in-out;
          }
          .side-menu--visible {
            transform: translateX(0%);
            opacity: 1;
            pointer-events: auto;
          }
          .side-menu-item {
            transition: all 0.2s ease;
          }
          .side-menu-item:hover {
            transform: translateX(4px);
          }
          .side-menu-item:active {
            transform: scale(0.98);
          }
          .facebook-card {
            background: var(--theme-surface);
            border-radius: 10px;
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.35);
            margin-bottom: 12px;
            overflow: hidden;
          }
          .menu-section-title {
            font-size: 16px;
            color: var(--theme-text-muted);
            padding: 16px 16px 4px;
            font-weight: 600;
          }
        `}
      </style>

      <div 
        className={`side-menu-overlay ${isOpen ? 'side-menu-overlay--visible' : ''}`}
        onClick={() => setIsOpen(false)}
      />

      <CSSTransition
        in={isOpen}
        timeout={300}
        classNames=""
        unmountOnExit={false}
        nodeRef={nodeRef}
      >
        <div 
          ref={(el) => { 
            nodeRef.current = el;
            menuRef.current = el;
          }} 
          className={`side-menu ${isOpen ? 'side-menu--visible' : ''}`}
        >
          <div className="sticky top-0 z-10 bg-[var(--theme-bg-soft)] px-4 py-3 flex items-center justify-between shadow-sm border-b border-gray-700/60">

          </div>

          <div className="px-4 py-3">
            {user && (
              <div className="facebook-card">
                <div className="p-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center mr-3">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-gray-900 font-medium">{user.contact_name || user.name}</p>
                      <p className="text-gray-500 text-sm">{user.email}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {user && (
              <div className="facebook-card">
                <h3 className="menu-section-title">{t('myProfiles')}</h3>
                
                <button
                  onClick={handleSwitchToUserProfile}
                  className={`flex items-center w-full p-3 text-left side-menu-item
                    ${user.role === 'user' ? 'bg-primary-50' : ''}`}
                  disabled={loading}
                >
                  <div className={`w-9 h-9 rounded-full mr-3 flex items-center justify-center 
                    ${user.role === 'user' 
                      ? 'bg-primary-100 text-primary-600' 
                      : 'bg-gray-100 text-gray-600'}`}
                  >
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">{t('regularUser')}</p>
                    {user.role === 'user' && (
                      <p className="text-xs text-primary-600">{t('active')}</p>
                    )}
                  </div>
                </button>
                
                <button
                  onClick={handleSwitchToSellerProfile}
                  className={`flex items-center w-full p-3 text-left side-menu-item
                    ${user.role === 'seller' ? 'bg-primary-50' : ''}`}
                  disabled={loading}
                >
                  <div className={`w-9 h-9 rounded-full mr-3 flex items-center justify-center 
                    ${user.role === 'seller' 
                      ? 'bg-primary-100 text-primary-600' 
                      : 'bg-gray-100 text-gray-600'}`}
                  >
                    <ShoppingCart className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-900 font-medium">
                      {hasSellerProfile ? t('sellerPage') : t('createSellerProfile')}
                    </p>
                    {user.role === 'seller' && (
                      <p className="text-xs text-primary-600">{t('active')}</p>
                    )}
                    {!hasSellerProfile && (
                      <p className="text-xs text-gray-500">{t('tapToCreate')}</p>
                    )}
                  </div>
                  {!hasSellerProfile && (
                    <Plus className="w-5 h-5 text-primary-500" />
                  )}
                </button>
                
                <button
                  onClick={handleSwitchToProviderProfile}
                  className={`flex items-center w-full p-3 text-left side-menu-item
                    ${user.role === 'provider' ? 'bg-primary-50' : ''}`}
                  disabled={loading}
                >
                  <div className={`w-9 h-9 rounded-full mr-3 flex items-center justify-center 
                    ${user.role === 'provider' 
                      ? 'bg-primary-100 text-primary-600' 
                      : 'bg-gray-100 text-gray-600'}`}
                  >
                    <Briefcase className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-900 font-medium">
                      {hasProviderProfile ? t('providerPage') : t('createProviderProfile')}
                    </p>
                    {user.role === 'provider' && (
                      <p className="text-xs text-primary-600">{t('active')}</p>
                    )}
                    {!hasProviderProfile && (
                      <p className="text-xs text-gray-500">{t('tapToCreate')}</p>
                    )}
                  </div>
                  {!hasProviderProfile && (
                    <Plus className="w-5 h-5 text-primary-500" />
                  )}
                </button>
              </div>
            )}

            {navLinks.length > 0 && (
              <div className="facebook-card">
                <h3 className="menu-section-title">{t('menu')}</h3>
                
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center p-3 side-menu-item"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="relative w-9 h-9 rounded-full bg-gray-100 mr-3 flex items-center justify-center text-gray-600">
                      {link.icon}
                      {link.badge > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1">
                          {link.badge > 99 ? '99+' : link.badge}
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-900 font-medium">{link.label}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </Link>
                ))}
              </div>
            )}
            
            <div className="facebook-card">
              <h3 className="menu-section-title">{t('account')}</h3>
              
              {authLinks.map((link) =>
                link.to ? (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center p-3 side-menu-item"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="w-9 h-9 rounded-full bg-gray-100 mr-3 flex items-center justify-center text-gray-600">
                      {link.icon}
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-900 font-medium">{link.label}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </Link>
                ) : (
                  <button
                    key={link.label}
                    onClick={() => {
                      link.onClick();
                      setIsOpen(false);
                    }}
                    className="flex items-center w-full p-3 text-left side-menu-item"
                  >
                    <div className="w-9 h-9 rounded-full bg-gray-100 mr-3 flex items-center justify-center text-gray-600">
                      {link.icon}
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-900 font-medium">{link.label}</p>
                    </div>
                  </button>
                )
              )}
            </div>

            <div className="facebook-card">
              <h3 className="menu-section-title">{t('theme')}</h3>
              <div className="flex gap-2 px-4 pb-3">
                {themeOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setTheme(opt.value)}
                    className={`flex-1 flex flex-col items-center gap-1.5 py-2.5 px-2 rounded-lg border transition-all ${
                      theme === opt.value
                        ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-400'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div
                      className="w-8 h-8 rounded-full border-2 flex items-center justify-center"
                      style={{
                        backgroundColor: opt.preview,
                        borderColor: theme === opt.value ? 'var(--theme-primary)' : 'var(--theme-border)',
                      }}
                    >
                      <span className={opt.value === 'light' ? 'text-gray-700' : 'text-white'}>
                        {opt.icon}
                      </span>
                    </div>
                    <span className={`text-xs font-medium ${
                      theme === opt.value ? 'text-primary-600' : 'text-gray-600'
                    }`}>
                      {opt.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="facebook-card">
              <h3 className="menu-section-title">{t('language') || 'Language'}</h3>
              <div className="flex gap-2 px-4 pb-3">
                <button
                  onClick={() => { setLanguage('en'); localStorage.setItem('language', 'en'); }}
                  className={`flex-1 py-2.5 px-3 rounded-lg border text-sm font-medium transition-all ${
                    language === 'en'
                      ? 'border-primary-500 bg-primary-50 text-primary-600 ring-1 ring-primary-400'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  English
                </button>
                <button
                  onClick={() => { setLanguage('am'); localStorage.setItem('language', 'am'); }}
                  className={`flex-1 py-2.5 px-3 rounded-lg border text-sm font-medium transition-all ${
                    language === 'am'
                      ? 'border-primary-500 bg-primary-50 text-primary-600 ring-1 ring-primary-400'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  አማርኛ
                </button>
              </div>
            </div>

            <div className="facebook-card">
              <h3 className="menu-section-title">{t('helpSupport')}</h3>
              
              <div className="grid grid-cols-2 gap-1">
                {footerLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex flex-col items-center p-3 side-menu-item"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="w-9 h-9 rounded-full bg-gray-100 mb-1 flex items-center justify-center text-gray-600">
                      {link.icon}
                    </div>
                    <p className="text-sm text-gray-700 text-center">{link.label}</p>
                  </Link>
                ))}
              </div>
            </div>
            
            <div className="mt-4 text-center text-xs text-gray-500">
              <p>© 2026 Warka</p>
              <p className="mt-1">v1.0.5</p>
            </div>
          </div>
        </div>
      </CSSTransition>

      {editingForm === 'provider' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <GoogleMapsProvider>
              <ProviderProfileForm
                initialData={{}}
                onSubmit={handleProfileUpdate}
                onGalleryUpdate={() => {}}
                onCancel={() => setEditingForm('')}
              />
            </GoogleMapsProvider>
          </div>
        </div>
      )}

      {editingForm === 'seller' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <GoogleMapsProvider>           
              <SellerProfileForm
                initialData={{}}
                onSubmit={handleSellerProfileUpdate}
                onCancel={() => setEditingForm('')}
                selectedLocation={selectedLocation}
                setSelectedLocation={setSelectedLocation}
                pickupLocations={pickupLocations}
                setPickupLocations={setPickupLocations}
              />
            </GoogleMapsProvider>
          </div>
        </div>
      )}

      {showAccountCenter && <AccountCenterModal />}
    </>
  );
};
